import React, { useCallback } from 'react';
import { ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import type { KpiData } from './types';
import type { KpiDrilldownSpec, KpiHeatmapCustomerApiData } from './kpiDashboardApi';
import { formatKpiTwoDecimalDisplay } from './kpiDashboardNumberFormat';
import { SectionLabel } from './KpiDashboardContent';
import { KpiBacklogBarChart, KpiPrioritySlaChart } from './charts';
import KpiIncidentCommComplianceDonuts from './charts/KpiIncidentCommComplianceDonuts';
import KpiIncidentAssignmentGroupComboChart from './charts/KpiIncidentAssignmentGroupComboChart';
import KpiRequestsByCustomerTable from './KpiRequestsByCustomerTable';
import KpiRequestsWeeklyLineChart from './charts/KpiRequestsWeeklyLineChart';

export interface KpiServiceRequestDashboardTabProps {
  chartsData: KpiData | undefined;
  chartsLoading: boolean;
  chartsFetching: boolean;
  chartsError: Error | null;
  chartsOnRetry?: () => void;
  heatmapData: KpiHeatmapCustomerApiData | undefined;
  heatmapLoading: boolean;
  heatmapFetching: boolean;
  heatmapError: Error | null;
  heatmapOnRetry?: () => void;
  onDrill: (spec: KpiDrilldownSpec) => void;
}

function RequestKpiHeaderStrip({
  requestsCount,
  avgOpenDays,
  mttr,
  onMetric,
}: {
  requestsCount: string;
  avgOpenDays: string;
  mttr: string;
  onMetric: (key: string) => void;
}) {
  const items = [
    { label: 'Requests Total KPI', value: requestsCount, drillKey: 'requests_volume' as const },
    { label: 'Requests Avg Open Days', value: avgOpenDays, drillKey: 'avg_open_days' as const },
    { label: 'MTTR (Minutes)', value: mttr, drillKey: 'requests_mttr' as const },
  ];

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-sm shadow-slate-900/5 ring-1 ring-slate-950/[0.04]">
      <div className="h-1 bg-gradient-to-r from-[#2F5597] via-[#4472C4] to-sky-500" aria-hidden />
      <div className="grid grid-cols-1 divide-y divide-slate-100 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
        {items.map((item, i) => (
          <button
            key={item.label}
            type="button"
            onClick={() => onMetric(item.drillKey)}
            className="flex flex-col items-start px-5 py-5 text-left transition-colors hover:bg-slate-50/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#4472C4] sm:px-6"
          >
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">{item.label}</span>
            <span
              className={`mt-1 text-3xl font-bold tabular-nums tracking-tight text-slate-900 ${
                i === 0 ? 'underline decoration-slate-900 decoration-1 underline-offset-4' : ''
              }`}
            >
              {item.value}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

const KpiServiceRequestDashboardTab: React.FC<KpiServiceRequestDashboardTabProps> = ({
  chartsData,
  chartsLoading,
  chartsFetching,
  chartsError,
  chartsOnRetry,
  heatmapData,
  heatmapLoading,
  heatmapFetching,
  heatmapError,
  heatmapOnRetry,
  onDrill,
}) => {
  const openMetric = useCallback(
    (key: string) => {
      if (key === 'requests_volume') onDrill({ kind: 'metric', key: 'requests_volume' });
      else if (key === 'avg_open_days') onDrill({ kind: 'metric', key: 'avg_open_days' });
      else if (key === 'requests_mttr') onDrill({ kind: 'metric', key: 'requests_mttr' });
    },
    [onDrill]
  );

  const openRate = useCallback(
    (key: string) => {
      onDrill({ kind: 'rate', key });
    },
    [onDrill]
  );

  const openPriority = useCallback(
    (priority: string) => {
      onDrill({ kind: 'priority', priority });
    },
    [onDrill]
  );

  const openBacklog = useCallback(
    (bucketKey: string) => {
      onDrill({ kind: 'backlog', bucket: bucketKey });
    },
    [onDrill]
  );

  const openAssignmentGroup = useCallback(
    (groupName: string) => {
      onDrill({ kind: 'assignment_group', groupName, ticketType: 'SERVICE_REQUEST' });
    },
    [onDrill]
  );

  const openTreemapCustomer = useCallback(
    (customerId: string) => {
      onDrill({ kind: 'kpi_volume_customer', customerId, ticketType: 'SERVICE_REQUEST' });
    },
    [onDrill]
  );

  const loading = (chartsLoading && !chartsData) || (heatmapLoading && !heatmapData);
  if (loading) {
    return (
      <div className="space-y-8" aria-busy="true" aria-label="Loading service request dashboard">
        <div className="h-24 animate-pulse rounded-xl bg-slate-200/80" />
        <div className="grid max-h-[500px] gap-4 lg:grid-cols-12">
          <div className="h-72 min-h-0 animate-pulse rounded-xl bg-slate-200/80 lg:col-span-7" />
          <div className="h-72 min-h-0 animate-pulse rounded-xl bg-slate-200/80 lg:col-span-5" />
        </div>
      </div>
    );
  }

  const blockingError = chartsError && !chartsData;
  const heatmapBlocking = heatmapError && !heatmapData;
  if (blockingError || heatmapBlocking) {
    const err = chartsError || heatmapError;
    return (
      <div className="rounded-xl border border-red-200/80 bg-white p-8 text-center shadow-sm">
        <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-red-500" aria-hidden />
        <p className="mt-3 text-sm font-semibold text-red-900">Could not load service request dashboard</p>
        <p className="mt-1 text-sm text-red-700/90">{err?.message}</p>
        {(chartsOnRetry || heatmapOnRetry) && (
          <button
            type="button"
            onClick={() => {
              chartsOnRetry?.();
              heatmapOnRetry?.();
            }}
            className="mt-4 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
          >
            Try again
          </button>
        )}
      </div>
    );
  }

  if (!chartsData || !heatmapData) return null;

  const { volumeByWeek } = heatmapData;

  const ongoingRate = chartsData.requestOngoingSlaMetRate;
  const totalSla = chartsData.requestOngoingSlaTicketCount;

  const resolutionRate = chartsData.requestResolutionSlaMetRate;
  const totalResolutionSla = chartsData.requestResolutionSlaTicketCount;
  const resolutionRateNum = parseFloat(resolutionRate);
  const resolutionCompliant =
    Number.isFinite(resolutionRateNum) && totalResolutionSla > 0
      ? Math.round((totalResolutionSla * resolutionRateNum) / 100)
      : 0;
  const resolutionNonCompliant = Math.max(0, totalResolutionSla - resolutionCompliant);

  const ongoingRateNum = parseFloat(ongoingRate);
  const ongoingCompliant =
    Number.isFinite(ongoingRateNum) && totalSla > 0 ? Math.round((totalSla * ongoingRateNum) / 100) : 0;
  const ongoingNonCompliant = Math.max(0, totalSla - ongoingCompliant);

  const requestsAvgOpen =
    chartsData.requestsAvgOpenDays !== '—' ? chartsData.requestsAvgOpenDays : chartsData.ticketsAvgOpenDays;
  const requestsMttr =
    chartsData.requestsMttrMinutes !== '—' ? chartsData.requestsMttrMinutes : chartsData.mttrMinutes;

  return (
    <div className="relative">
      {(chartsFetching || heatmapFetching) && (
        <div
          className="pointer-events-none absolute right-0 top-0 z-10 flex items-center gap-1.5 rounded-full border border-indigo-200/80 bg-white/95 px-3 py-1.5 text-xs font-medium text-indigo-800 shadow-md shadow-indigo-900/10 backdrop-blur-sm"
          aria-live="polite"
        >
          <ArrowPathIcon className="h-3.5 w-3.5 animate-spin text-indigo-600" aria-hidden />
          Updating
        </div>
      )}
      {(chartsError || heatmapError) && chartsData && heatmapData && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-amber-200/90 bg-amber-50/90 px-4 py-3 text-sm text-amber-950 shadow-sm">
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-amber-600" aria-hidden />
          <span className="min-w-0 flex-1">
            Showing cached data. Latest refresh failed: {(chartsError || heatmapError)?.message}
          </span>
          <button
            type="button"
            onClick={() => {
              chartsOnRetry?.();
              heatmapOnRetry?.();
            }}
            className="shrink-0 font-semibold text-amber-900 underline decoration-amber-600/50 underline-offset-2 hover:text-amber-950"
          >
            Retry
          </button>
        </div>
      )}

      <section className="mb-6">
        <SectionLabel>Service request KPIs</SectionLabel>
        <RequestKpiHeaderStrip
          requestsCount={chartsData.requestsVolume.toLocaleString()}
          avgOpenDays={formatKpiTwoDecimalDisplay(requestsAvgOpen)}
          mttr={formatKpiTwoDecimalDisplay(requestsMttr)}
          onMetric={openMetric}
        />
      </section>

      <section className="mb-6">
        <SectionLabel>Requests by customer &amp; SLA</SectionLabel>
        <div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-12 lg:max-h-[500px] lg:min-h-0 lg:overflow-hidden">
          <div className="flex h-full min-h-[280px] flex-col lg:col-span-8 lg:min-h-0 lg:max-h-[500px]">
            <KpiRequestsByCustomerTable
              rows={chartsData.requestsByCustomerTreemap}
              onRowClick={openTreemapCustomer}
            />
          </div>
          <div className="flex h-full min-h-[280px] flex-col lg:col-span-4 lg:min-h-0 lg:max-h-[500px]">
            <KpiIncidentCommComplianceDonuts
              ongoingCommRatePct={ongoingRate}
              ongoingCommTicketCount={totalSla}
              ongoingCompliantCount={ongoingCompliant}
              ongoingNonCompliantCount={ongoingNonCompliant}
              compliantCount={resolutionCompliant}
              nonCompliantCount={resolutionNonCompliant}
              onOngoingCompliantSliceDrill={() => openRate('request_ongoing_met')}
              onOngoingNonCompliantSliceDrill={() => openRate('request_ongoing_breached')}
              onOngoingCenterDrill={() => openRate('request_ongoing')}
              onCompliantSliceDrill={() => openRate('request_resolution_met')}
              onNonCompliantSliceDrill={() => openRate('request_resolution_breached')}
              onResolutionCenterDrill={() => openRate('request_resolution')}
              ongoingHeaderText="Request Ongoing comm SLA"
              complianceHeaderText="Request Resolution SLA"
              panelClassName="lg:flex-1 lg:overflow-hidden"
            />
          </div>
        </div>
      </section>

      <section className="mb-6">
        <SectionLabel>Requests by week &amp; backlog age</SectionLabel>
        <div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-2">
          <KpiRequestsWeeklyLineChart volumeByWeek={volumeByWeek} onDrill={onDrill} />
          <KpiBacklogBarChart
            title="Requests Backlog Age Buckets"
            data={chartsData.backlogAge}
            onBucketClick={openBacklog}
          />
        </div>
      </section>

      <section className="mb-6">
        <SectionLabel>Assignment group &amp; priority</SectionLabel>
        <div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-2">
          <KpiIncidentAssignmentGroupComboChart
            rows={chartsData.requestsByAssignmentGroup}
            onGroupClick={openAssignmentGroup}
            panelTitle="Requests by Assignment Group w/ SLA Breach %"
            emptyMessage="No service requests for the selected filters."
          />
          <KpiPrioritySlaChart
            title="Requests By Priority Level"
            priorityCounts={chartsData.priorityCounts}
            slaBreachPct={chartsData.slaBreachPct}
            onPriorityBarClick={openPriority}
          />
        </div>
      </section>
    </div>
  );
};

export default KpiServiceRequestDashboardTab;
